import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Layers, User, Clapperboard, Globe, BookOpen,
  Plus, ArrowRight, ArrowLeft, Film, FileText, Book,
  Tv, TrendingUp, TrendingDown, Minus, FileDown, History, Upload,
  ChevronRight, Network, Gauge, Pencil, Trash2, Brain, Send, Loader2, X, ScrollText } from 'lucide-react';
import VersionHistoryModal from '../components/VersionHistoryModal';
import ConflictGauge from '../components/ConflictGauge';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import Sidebar from '../components/Sidebar';
import { useProject } from '../context/ProjectContext';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { isPaidTier } from '../lib/pricing';
import { useTranslation } from 'react-i18next';
import NotesTab from './NotesTab';
import WorldBuilding from './WorldBuilding';
import SplitViewLayout, { SplitViewTrigger, SPLIT_TAB_LABELS } from '../components/SplitViewLayout';
import PlotCanvas from '../components/PlotCanvas';
import EmptyState from '../components/EmptyState';
import { SCROLL_CANVAS } from '../lib/featureFlags';
import { createScreenplaySave, seedScreenplayRev } from '../screenplay/saveClient';
import { serializeDocument } from '../screenplay/model';
import FountainImportPanel from '../screenplay/FountainImportPanel';
import { serializeFountain, screenplaySafeName } from '../screenplay/fountainSerializer';

/* ── Screenplay export helpers (V2c Part 2) ─────────────────────────────
 * Shared by the Script tab buttons and the Export Center block. Pure
 * download plumbing — the interesting logic lives in fountainSerializer.js
 * (client, .fountain) and server.py's _render_screenplay_pdf (PDF). */

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/** Client-side .fountain download. Degradations (structures Fountain cannot
 * express) are surfaced, never silent — mirror of import's summary honesty. */
function exportFountainFile(elements, title) {
  const { text, notes } = serializeFountain(elements, { title });
  downloadBlob(
    new Blob([text], { type: 'text/plain;charset=utf-8' }),
    `${screenplaySafeName(title)}.fountain`,
  );
  const noteCount = Object.values(notes).reduce((a, b) => a + b, 0);
  if (noteCount > 0) {
    toast.warning(
      `Exported with ${noteCount} compatibility note${noteCount === 1 ? '' : 's'} — ` +
      'some structures have no exact Fountain form. Your text is fully preserved; '
      + 'element types may read differently on re-import.',
    );
  } else {
    toast.success('Fountain file exported!');
  }
}

/** Server-side screenplay PDF. `document` (the live editor doc) makes the
 * export exactly what the writer sees; omitted, the server uses the stored
 * copy (Export Center path). */
async function exportScreenplayPdf(projectId, title, document_) {
  const res = await authFetch(`${API}/export/screenplay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ project_id: projectId, document: document_ ?? null }),
  });
  if (!res.ok) {
    let msg = 'Export failed — try again';
    if (res.status >= 400 && res.status < 500) {
      const detail = await res.json().then((d) => d?.detail).catch(() => null);
      if (typeof detail === 'string' && detail) msg = detail.slice(0, 200);
    }
    throw Object.assign(new Error(msg), { userFacing: true });
  }
  downloadBlob(await res.blob(), `${screenplaySafeName(title)}_Script.pdf`);
  toast.success('Screenplay PDF exported!');
}

// Lazy: Lexical (~35KB gz) only downloads when the Script tab first opens —
// every other tab's users never pay for the editor.
const ScreenplayEditor = lazy(() => import('../screenplay/ScreenplayEditor'));

// Screenplay module — Phase 3: autosaved to the `screenplay` field on the
// plot project doc (V2b version history covers it automatically). Within a
// session the workspace ref (ctx.screenplayDocRef) is the freshest copy —
// it survives tab switches; the server doc seeds a fresh workspace visit.
// Header row matches every other module (title + description + split trigger).
// Exported (like CharactersTab/CharacterWebTab) so Series Builder can borrow
// it — a series project IS a plot-type project doc, so the whole stack
// (save path, import, export, version history) works there unchanged.
export function ScriptEditorUpgrade({ navigate }) {
  return (
    <div className="bg-card border border-border/40 p-16 text-center max-w-lg mx-auto mt-10" data-testid="script-editor-upgrade">
      <ScrollText className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
      <h3 className="font-mono text-xl font-semibold mb-3">The script editor is an Indie feature</h3>
      <p className="text-muted-foreground mb-6">
        Write, import and export industry-standard screenplays with autosave and version history.
        Upgrade to Indie (KES 499/mo) to open it.
      </p>
      <Button
        onClick={() => navigate('/upgrade')}
        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
      >
        Upgrade to Indie
      </Button>
    </div>
  );
}

export function ScreenplayTab({ splitProps, docRef, project }) {
  const projectId = project?.project_id;
  // The workspace's project comes from the /projects LIST, which projects
  // the (multi-MB) screenplay body OUT for dashboard performance. The
  // document is fetched here, per tab entry, from the detail endpoint —
  // the editor must NEVER mount unseeded next to a valid rev, or its first
  // autosave would overwrite the writer's script with emptiness.
  const [loaded, setLoaded] = useState(() =>
    docRef?.current !== undefined && docRef?.current !== null
      ? { screenplay: docRef.current, rev: undefined } // session draft wins
      : null,
  );
  useEffect(() => {
    if (loaded || !projectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/projects/${projectId}`);
        if (!res.ok) throw new Error(`load failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) {
          setLoaded({ screenplay: data.screenplay, rev: data.screenplay_rev });
        }
      } catch (err) {
        console.error('screenplay: document load failed', err);
        if (!cancelled) setLoaded({ error: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loaded, projectId]);

  // One save client per mounted tab: owns the wire contract (payload shape,
  // keepalive byte math, skip-if-unchanged) and the optimistic-concurrency
  // rev, rotated on every successful save.
  //
  // Rev seeding priority (review F4 — the spurious-409 fix):
  //   1. docRef.rev — the rev THIS session's own autosaves last advanced to,
  //      surviving a tab round-trip in the same ref channel as the doc. On a
  //      session-draft remount `loaded.rev` is undefined and the detail
  //      fetch is skipped, so without this the client fell back to the stale
  //      page-load list rev (step 3) and 409'd the writer's next save with
  //      no real conflict.
  //   2. loaded.rev — the detail-endpoint rev on a genuine first mount.
  //   3. project.screenplay_rev — the /projects LIST rev; last-resort seed,
  //      stale the moment any save rotates it.
  // onRev keeps docRef.rev current so (1) stays authoritative across mounts.
  const saveClientRef = useRef(null);
  if (projectId && loaded && !loaded.error && !saveClientRef.current) {
    saveClientRef.current = createScreenplaySave(
      API,
      projectId,
      seedScreenplayRev(docRef, loaded, project?.screenplay_rev),
      (newRev) => { if (docRef) docRef.rev = newRev; },
    );
  }
  const handleSave = useCallback(
    (doc, meta) => saveClientRef.current.save(doc, meta),
    [],
  );

  const [importOpen, setImportOpen] = useState(false);
  // "Is there anything an import could destroy?" must be answered from EVERY
  // copy of the truth, not just the tab-mount fetch: docRef.current is the
  // live in-session document (fresh after typing that `loaded` never sees),
  // `loaded` is the server doc at tab entry, and a non-null rev means the
  // server has taken at least one screenplay write this project's lifetime
  // (covers the deleted-in-session-draft case). Deriving this from `loaded`
  // alone let a session's typed work be overwritten with no warning and no
  // snapshot (adversarial review F1). Over-warning on an emptied-but-saved
  // script is the acceptable direction of error.
  const importCouldDestroy = Boolean(
    docRef?.current?.elements?.length ||
    loaded?.screenplay?.elements?.length ||
    loaded?.rev ||
    project?.screenplay_rev,
  );
  // One safety snapshot per panel-open: content cannot change while the
  // panel is open (the editor is unmounted), so retries after a failed save
  // must NOT re-snapshot — each manual snapshot prunes history to its cap,
  // and a retry loop would evict every older restore point (review F6).
  const importSnapshotTakenRef = useRef(false);
  // Reused verbatim from the V2b restore flow: kill the (about-to-be-unmounted)
  // editor's autosave scheduler before reload, so its beforeunload flush can
  // never race the reload with stale pre-import content.
  const handleImportConfirm = useCallback(async (elements) => {
    const doc = serializeDocument(elements);
    if (importCouldDestroy && !importSnapshotTakenRef.current) {
      // The screenplay PUT route's own snapshot hook is cooldown-throttled
      // (10 min, tuned for autosave ticks) — it can silently coalesce away,
      // which would leave THIS overwrite unrecoverable despite the summary
      // gate promising a restorable snapshot. Force one explicitly first,
      // same discipline as restore_snapshot: abort rather than overwrite
      // with no safety net if the snapshot itself can't be taken.
      // purpose=import → a grace-protected pre_import safety net, so the
      // undo-the-import window survives a long post-import session (review F7).
      const snapRes = await authFetch(`${API}/projects/${projectId}/snapshots?purpose=import`, { method: 'POST' });
      if (!snapRes.ok) {
        throw new Error("Couldn't save a safety snapshot of your current script — import cancelled, nothing was changed.");
      }
      importSnapshotTakenRef.current = true;
    }
    await saveClientRef.current.save(doc);
    window.dispatchEvent(new Event('pangaji:screenplay-suppress-flush'));
    window.location.reload();
  }, [importCouldDestroy, projectId]);

  // ── Export (V2c Part 2) ──────────────────────────────────────────────
  // Both paths export the LIVE document (docRef is fresher than `loaded`
  // after any typing) — what you see is exactly what you export.
  const [exporting, setExporting] = useState(false);
  const liveDocument = () => docRef?.current ?? loaded?.screenplay ?? null;

  const handleExportFountain = () => {
    const doc = liveDocument();
    if (!doc?.elements?.length) {
      toast.error('Nothing to export yet — write something first.');
      return;
    }
    try {
      exportFountainFile(doc.elements, project?.title);
    } catch (err) {
      toast.error('Export failed — try again');
      console.error('screenplay: fountain export failed', err);
    }
  };

  const handleExportPdf = async () => {
    const doc = liveDocument();
    if (!doc?.elements?.length) {
      toast.error('Nothing to export yet — write something first.');
      return;
    }
    setExporting(true);
    try {
      await exportScreenplayPdf(projectId, project?.title, doc);
    } catch (err) {
      toast.error(err?.userFacing ? err.message : 'Export failed — try again');
    } finally {
      setExporting(false);
    }
  };

  let body;
  if (importOpen) {
    // Renders INSTEAD OF the editor (not an overlay on top of it) — this
    // unmounts the live editor's autosave scheduler for the duration of the
    // import, so nothing can race the import's own save through the same
    // save client.
    body = (
      <FountainImportPanel
        hasExistingContent={importCouldDestroy}
        onCancel={() => setImportOpen(false)}
        onConfirm={handleImportConfirm}
      />
    );
  } else if (!loaded) {
    body = (
      <div className="p-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">
        Loading your screenplay…
      </div>
    );
  } else if (loaded.error) {
    body = (
      <div className="p-8 font-mono text-xs uppercase tracking-widest text-destructive">
        Couldn't load your screenplay — check your connection and reopen this tab.
      </div>
    );
  } else {
    body = (
      <Suspense
        fallback={
          <div className="p-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Loading editor…
          </div>
        }
      >
        <ScreenplayEditor
          initialDocument={loaded.screenplay}
          initialDirty={Boolean(docRef?.unsaved)}
          onDocChange={(doc, meta) => {
            if (!docRef) return;
            docRef.current = doc;
            // Survives as "unsaved" until a save actually lands — the next
            // mount re-attempts instead of showing Saved over a document
            // the server does not have.
            docRef.unsaved = meta?.unsaved ?? false;
          }}
          onSave={projectId ? handleSave : undefined}
        />
      </Suspense>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">Screenplay</h2>
          <p className="text-muted-foreground">
            Write in industry-standard format — pick an element type, start typing.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {loaded && !loaded.error && !importOpen && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPdf}
                disabled={exporting}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                {exporting
                  ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                  : <FileDown className="w-4 h-4 sm:mr-2" />}
                <span className="hidden sm:inline">Export PDF</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportFountain}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                <FileText className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">.fountain</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  importSnapshotTakenRef.current = false; // fresh panel, fresh net
                  setImportOpen(true);
                }}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                <Upload className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Import Fountain</span>
              </Button>
            </>
          )}
          {splitProps && <SplitViewTrigger {...splitProps} />}
        </div>
      </div>
      <div className="bg-card border border-border/40">{body}</div>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMAT_LABELS = {
  feature:      'Feature Film',
  feature_film: 'Feature Film',
  short:        'Short Film',
  short_film:   'Short Film',
  novel:        'Novel',
  series:       'Series',
  tv_series:    'TV Series',
  tv_episode:   'TV Episode',
  tv_pilot:     'Pilot',
};

const FORMAT_ICONS = {
  feature:      Film,
  feature_film: Film,
  short:        FileText,
  short_film:   FileText,
  novel:        Book,
  series:       Tv,
  tv_series:    Tv,
  tv_episode:   Tv,
  tv_pilot:     Tv,
};

// Tabs this workspace can actually render — mirrors renderTab(). Used to
// validate the URL param and any persisted split_view_preference (which may
// have been saved by Series Builder with series-only tabs like "episodes").
const WORKSPACE_TABS = Object.keys(SPLIT_TAB_LABELS);

const ARC_ICONS = {
  positive: TrendingUp,
  negative: TrendingDown,
  flat:     Minus,
};

// Map a tab id → its rendered component, given the workspace data bag.
// Each module receives `splitProps` so it can render the trigger inside its
// own header row — there is no longer a workspace-level split bar.
function renderTab(tab, ctx) {
  const splitProps = ctx.splitProps;
  switch (tab) {
    case 'plot':
      return <PlotTab project={ctx.plotProject} user={ctx.user} navigate={ctx.navigate} splitProps={splitProps} />;
    case 'characters':
      return <CharactersTab characters={ctx.characters} plotProject={ctx.plotProject} navigate={ctx.navigate} onRefresh={ctx.onRefresh} splitProps={splitProps} />;
    case 'scenes':
      return <ScenesTab scenes={ctx.scenes} plotProject={ctx.plotProject} navigate={ctx.navigate} splitProps={splitProps} />;
    case 'bible':
      return <BibleTab onExport={ctx.handleExportPDF} exporting={ctx.exporting} tier={ctx.tier} splitProps={splitProps} />;
    case 'notes':
      return <NotesTab project={ctx.plotProject} navigate={ctx.navigate} splitProps={splitProps} />;
    case 'world':
      // Render WorldBuilding inline (embedded mode) so it participates in
      // the workspace tab system and can be paired in split view.
      return <WorldBuilding embeddedProjectId={ctx.plotProject?.project_id} splitProps={splitProps} />;
    case 'web':
      return (
        <CharacterWebTab
          characters={ctx.characters}
          relationships={ctx.relationships}
          setRelationships={ctx.setRelationships}
          plotProject={ctx.plotProject}
          user={ctx.user}
          navigate={ctx.navigate}
          splitProps={splitProps}
        />
      );
    case 'screenplay':
      if (ctx.user && !isPaidTier(ctx.user.subscription_tier)) {
        // Server 403s Spark on save/export — show the upgrade path instead of
        // an editor that says "NOT SAVING".
        return <ScriptEditorUpgrade navigate={ctx.navigate} />;
      }
      return (
        <ScreenplayTab
          splitProps={splitProps}
          docRef={ctx.screenplayDocRef}
          project={ctx.plotProject}
        />
      );
    default:
      return <div className="text-muted-foreground font-mono">Unknown tab: {tab}</div>;
  }
}

const ACTS = [
  { level: 1, labelKey: 'ws_act1', subtitleKey: 'ws_act1Desc' },
  { level: 2, labelKey: 'ws_act2', subtitleKey: 'ws_act2Desc' },
  { level: 3, labelKey: 'ws_act3', subtitleKey: 'ws_act3Desc' },
];

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setActiveProject } = useProject();
  const { t } = useTranslation();
  // Screenplay draft survives tab switches within this workspace session —
  // the editor serializes into this ref on unmount (Phase 1: no persistence).
  const screenplayDocRef = useRef(null);

  const rawTab = searchParams.get('tab') || 'plot';
  const tab = WORKSPACE_TABS.includes(rawTab) ? rawTab : 'plot';

  const [user, setUser] = useState(null);
  const [plotProject, setPlotProject] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [splitView, setSplitView] = useState(null); // null | { left: tab, right: tab }

  // Hydrate split view from project on load. Only restore pairings this
  // workspace can render — a preference saved in Series Builder can reference
  // series-only tabs; silently ignore those instead of rendering a broken pane.
  // Phone-width sessions (below Tailwind md) skip hydration entirely: a split
  // saved on desktop must not reopen as the stacked switcher on mobile. The
  // user can still open a split manually from a module's trigger.
  useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) return;
    const pref = plotProject?.split_view_preference;
    if (pref?.right && WORKSPACE_TABS.includes(pref.right)) {
      setSplitView(pref);
    }
  }, [plotProject?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist split view changes — fire-and-forget; UI updates immediately
  const updateSplitView = async (next) => {
    setSplitView(next);
    try {
      await authFetch(`${API}/projects/${projectId}/split-view`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(next || { left: null, right: null }),
      });
    } catch (e) {
      console.error('split-view persist failed', e);
    }
  };

  const tier = user?.subscription_tier || 'spark';

  useEffect(() => {
    fetchAll();
  }, [projectId]);

  // World tab now renders inline via WorldBuilding's embeddedProjectId mode
  // (was: redirect to /world-building) — keeps the workspace context intact
  // and lets World Building participate in split view.

  const fetchAll = async () => {
    try {
      const [userRes, projectsRes, scenesRes] = await Promise.all([
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' }),
        authFetch(`${API}/scenes?plot_project_id=${projectId}`, { credentials: 'include' }),
      ]);

      const userData = await userRes.json();
      const projectsData = await projectsRes.json();
      const scenesData = await scenesRes.json();

      setUser(userData);
      setScenes(Array.isArray(scenesData) ? scenesData : []);

      if (['indie', 'studio'].includes(userData.subscription_tier)) {
        const relsRes = await authFetch(`${API}/relationships`, { credentials: 'include' });
        const relsData = await relsRes.json();
        setRelationships(Array.isArray(relsData) ? relsData : []);
      }

      const plot = Array.isArray(projectsData)
        ? projectsData.find(p => p.project_id === projectId && p.project_type === 'plot')
        : null;

      if (!plot) {
        navigate('/dashboard?type=plot');
        return;
      }

      setPlotProject(plot);

      // Set global project context so child pages (SceneBuilder, WorldBuilding) know the active workspace
      setActiveProject({
        plotProjectId: plot.project_id,
        projectTitle: plot.title,
        projectFormat: plot.format,
      });

      // Filter characters by workspace_id to scope to this workspace
      const allChars = Array.isArray(projectsData) ? projectsData.filter(p => p.project_type === 'character') : [];
      const workspaceChars = allChars.filter(c => c.workspace_id === projectId);
      setCharacters(workspaceChars);
    } catch (err) {
      toast.error('Failed to load workspace');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => setExportModalOpen(true);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading workspace...</div>
      </div>
    );
  }

  if (!plotProject) return null;

  const FormatIcon = FORMAT_ICONS[plotProject.format] || Film;

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <div className="noise-overlay" />

      <Sidebar
        userTier={tier}
        projectId={projectId}
        projectTitle={plotProject.title}
        onExportBible={handleExportPDF}
        exportingBible={exporting}
      />

      {/* min-w-0: without it this flex child grows to fit the fixed-width
          screenplay page and drags the whole layout past the viewport —
          the editor surface must scroll internally instead. */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="glassmorphism sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard?type=plot')}
                className="rounded-none font-mono uppercase tracking-widest text-xs px-2 sm:px-4 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('dash_title')}</span>
              </Button>
              <div className="w-px h-5 bg-border/40 hidden sm:block" />
              <div className="flex items-center gap-2 min-w-0">
                <FormatIcon className="w-4 h-4 text-primary flex-shrink-0" />
                <h1 className="font-mono text-lg font-semibold tracking-tight truncate max-w-[40vw] sm:max-w-xs">
                  {plotProject.title}
                </h1>
                <span className="hidden sm:inline-block font-mono text-xs text-muted-foreground uppercase px-2 py-0.5 border border-border/40">
                  {FORMAT_LABELS[plotProject.format] || plotProject.format}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                <FileDown className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('sidebar_bible')}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
                aria-label="Version History"
                className="rounded-none font-mono uppercase tracking-widest text-xs"
              >
                <History className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Tab Content — each module renders its own SplitViewTrigger inside
            its h2 row, so there is no workspace-level split bar anymore. */}
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24 md:py-10 w-full">
          {splitView?.right && splitView.right !== tab && WORKSPACE_TABS.includes(splitView.right) ? (
            <SplitViewLayout
              leftLabel={SPLIT_TAB_LABELS[tab] || tab}
              rightLabel={SPLIT_TAB_LABELS[splitView.right] || splitView.right}
              onClose={() => updateSplitView(null)}
              left={renderTab(tab, {
                plotProject, characters, scenes, relationships, setRelationships,
                user, navigate, handleExportPDF, exporting, tier, screenplayDocRef,
                onRefresh: fetchAll,
                splitProps: { primaryTab: tab, splitView, onChangeSplit: updateSplitView, pane: 'left' },
              })}
              right={renderTab(splitView.right, {
                plotProject, characters, scenes, relationships, setRelationships,
                user, navigate, handleExportPDF, exporting, tier, screenplayDocRef,
                onRefresh: fetchAll,
                splitProps: { primaryTab: splitView.right, splitView, onChangeSplit: updateSplitView, pane: 'right' },
              })}
            />
          ) : (
            renderTab(tab, {
              plotProject, characters, scenes, relationships, setRelationships,
              user, navigate, handleExportPDF, exporting, tier, screenplayDocRef,
              onRefresh: fetchAll,
              splitProps: { primaryTab: tab, splitView, onChangeSplit: updateSplitView },
            })
          )}
        </main>
      </div>

      {exportModalOpen && (
        <ExportCenterModal
          project={plotProject}
          characters={characters}
          scenes={scenes}
          relationships={relationships}
          user={user}
          onClose={() => setExportModalOpen(false)}
        />
      )}

      {historyOpen && (
        <VersionHistoryModal
          projectId={projectId}
          onClose={() => setHistoryOpen(false)}
          onRestored={() => {
            // Kill any pending screenplay autosave/flush FIRST — the
            // reload's beforeunload would otherwise PUT the pre-restore
            // draft over the document the restore just wrote.
            window.dispatchEvent(new Event('pangaji:screenplay-suppress-flush'));
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

/* ─── Export Center Modal ────────────────────────────────── */
const EXPORT_SECTIONS = [
  { key: 'plot',          label: 'Plot / Beat Sheet',    description: 'Logline, synopsis, and all act beats' },
  { key: 'characters',    label: 'Characters',           description: 'Full character sheets and biography' },
  { key: 'scenes',        label: 'Scenes',               description: 'Scene list with motivations and notes' },
  { key: 'relationships', label: 'Relationships',        description: 'Character web and conflict data' },
  { key: 'world',         label: 'World Building',       description: 'Locations, society, economy, history' },
];

const EXPORT_FORMATS = [
  { key: 'pdf',  label: 'PDF',      ext: 'pdf', description: 'Formatted story bible' },
  { key: 'docx', label: 'Word',     ext: 'docx', description: 'Editable .docx' },
  { key: 'md',   label: 'Markdown', ext: 'md',  description: 'Portable plain text' },
  { key: 'txt',  label: 'Text',     ext: 'txt', description: 'Raw .txt' },
];

const BEAT_NAMES = {
  opening_image: 'First Frame', setup: 'The World Before', theme_stated: 'The Core Truth',
  catalyst: 'The Spark', debate: 'The Crossroads', break_into_two: 'The Leap',
  b_story: 'The Heart Thread', fun_and_games: 'The Promise', midpoint: 'The Turning Tide',
  bad_guys_close_in: 'The Walls Closing', all_is_lost: 'The Breaking Point', dark_night: 'The Hollow',
  break_into_three: 'The Rising', finale: 'The Reckoning', final_image: 'The Last Frame',
  obstacle: 'The Obstacle', escalation: 'The Escalation',
};

function buildPreview(sections, project, characters, scenes, relationships) {
  const lines = [];

  if (sections.includes('plot')) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push(`PLOT — ${project.title}`);
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    if (project.logline) lines.push(`Logline: ${project.logline}`);
    if (project.synopsis) lines.push(`\nSynopsis:\n${project.synopsis}`);
    [1, 2, 3].forEach(lvl => {
      const data = project[`level_${lvl}`] || {};
      const filled = Object.entries(data).filter(([, v]) => v);
      if (filled.length) {
        lines.push(`\nAct ${lvl}:`);
        filled.forEach(([k, v]) => lines.push(`  ${BEAT_NAMES[k] || k}: ${v}`));
      }
    });
    lines.push('');
  }

  if (sections.includes('characters') && characters.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('CHARACTERS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    characters.forEach(c => {
      const name = c.level_0?.name || c.title;
      lines.push(`\n${name} — ${(c.character_role || '').replace('_', ' ')} (${c.arc_type} arc)`);
      if (c.level_0?.biography) lines.push(`Bio: ${c.level_0.biography}`);
    });
    lines.push('');
  }

  if (sections.includes('scenes') && scenes.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('SCENES');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    scenes.forEach(s => {
      lines.push(`\n${s.title}${s.beat_key ? ` [${BEAT_NAMES[s.beat_key] || s.beat_key}]` : ''}${s.location ? ` — ${s.location}` : ''}`);
      if (s.motivation) lines.push(`  Motivation: ${s.motivation}`);
      if (s.obstacle)   lines.push(`  Obstacle: ${s.obstacle}`);
    });
    lines.push('');
  }

  if (sections.includes('relationships') && relationships.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('RELATIONSHIPS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    const nameMap = Object.fromEntries(characters.map(c => [c.project_id, c.level_0?.name || c.title]));
    relationships.forEach(r => {
      const a = nameMap[r.protagonist_id] || 'Character A';
      const b = nameMap[r.target_id] || 'Character B';
      lines.push(`\n${a} ↔ ${b}`);
      if (r.moral_difference)    lines.push(`  Moral difference: ${r.moral_difference}`);
      if (r.central_argument)    lines.push(`  Central argument: ${r.central_argument}`);
    });
    lines.push('');
  }

  if (sections.includes('world')) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('WORLD BUILDING');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('(Locations, society, economy, history and more will be included from your World Building data.)');
    lines.push('');
  }

  return lines.join('\n') || 'Select sections above to preview content.';
}

function ExportCenterModal({ project, characters, scenes, relationships, user, onClose }) {
  const { t } = useTranslation();
  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);
  const today = new Date().toISOString().split('T')[0];
  const safeTitle = (project.title || 'Story').replace(/[^a-zA-Z0-9]/g, '_');

  const [selectedSections, setSelectedSections] = useState(['plot', 'characters', 'scenes', 'relationships', 'world']);
  const [format, setFormat] = useState('pdf');
  const [filename, setFilename] = useState(`${safeTitle}_Bible_${today}`);
  const [downloading, setDownloading] = useState(false);
  const [scriptExporting, setScriptExporting] = useState(false); // false | 'pdf' | 'fountain'

  // Script exports from here use the SAVED document (this modal has no live
  // editor); the Script tab's own buttons cover the what-you-see path.
  const handleScriptPdf = async () => {
    setScriptExporting('pdf');
    try {
      await exportScreenplayPdf(project.project_id, project.title, null);
    } catch (err) {
      toast.error(err?.userFacing ? err.message : 'Export failed — try again');
    } finally {
      setScriptExporting(false);
    }
  };

  const handleScriptFountain = async () => {
    setScriptExporting('fountain');
    try {
      const res = await authFetch(`${API}/projects/${project.project_id}`);
      if (!res.ok) throw new Error(`load failed (${res.status})`);
      const data = await res.json();
      const elements = data?.screenplay?.elements;
      if (!elements?.length) {
        toast.error('Nothing to export yet — the screenplay is empty.');
        return;
      }
      exportFountainFile(elements, project.title);
    } catch (err) {
      toast.error('Export failed — try again');
      console.error('screenplay: fountain export failed', err);
    } finally {
      setScriptExporting(false);
    }
  };

  const activeFormat = EXPORT_FORMATS.find(f => f.key === format) || EXPORT_FORMATS[0];

  const toggleSection = (key) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleDownload = async () => {
    if (!selectedSections.length) { toast.error('Select at least one section'); return; }
    setDownloading(true);
    try {
      const response = await authFetch(`${API}/export/story-bible`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ project_id: project.project_id, sections: selectedSections, format }),
      });
      if (!response.ok) {
        // Surface the backend's reason (e.g. tier requirement) instead of a
        // generic retry prompt that can't succeed for 4xx failures. Only 4xx
        // detail strings are shown; 5xx bodies and network errors stay generic.
        let msg = 'Export failed — try again';
        if (response.status >= 400 && response.status < 500) {
          const detail = await response.json().then(d => d?.detail).catch(() => null);
          if (typeof detail === 'string' && detail) msg = detail.slice(0, 200);
        }
        const err = new Error(msg);
        err.userFacing = true;
        throw err;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${activeFormat.ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Story Bible exported!');
      onClose();
    } catch (err) {
      // Raw fetch/blob errors ("Failed to fetch") are not user-facing copy.
      toast.error(err?.userFacing ? err.message : 'Export failed — try again');
    } finally {
      setDownloading(false);
    }
  };

  const preview = buildPreview(selectedSections, project, characters, scenes, relationships);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-card border border-border/40 w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileDown className="w-5 h-5 text-primary" />
            <h2 className="font-mono text-lg font-semibold tracking-tight">{t('export_title')}</h2>
            {!isIndiePlus && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-border/40 px-2 py-0.5">
                {t('export_sparkNote')}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-y-auto md:overflow-visible">
          {/* Left panel — controls. Stacks above the preview below md. */}
          <div className="w-full md:w-72 flex-shrink-0 border-b md:border-b-0 md:border-r border-border/40 flex flex-col p-6 gap-6 md:overflow-y-auto">
            {/* Section checkboxes */}
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">{t('export_sections')}</p>
              <div className="space-y-2">
                {EXPORT_SECTIONS.map(sec => (
                  <label key={sec.key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(sec.key)}
                      onChange={() => toggleSection(sec.key)}
                      className="mt-0.5 accent-primary"
                    />
                    <div>
                      <p className="font-mono text-sm font-medium group-hover:text-primary transition-colors">
                        {sec.label}
                      </p>
                      <p className="text-xs text-muted-foreground">{sec.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Format selector */}
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Format</p>
              <div className="grid grid-cols-2 gap-2">
                {EXPORT_FORMATS.map(fmt => {
                  const active = format === fmt.key;
                  return (
                    <button
                      key={fmt.key}
                      type="button"
                      onClick={() => setFormat(fmt.key)}
                      className={`border px-3 py-2 text-left transition-colors ${
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border/40 text-foreground hover:border-primary/60'
                      }`}
                    >
                      <span className="block font-mono text-sm font-medium">{fmt.label}</span>
                      <span className="block text-[10px] text-muted-foreground leading-tight">{fmt.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Screenplay export — its own pipeline, separate from the bible
                sections above (V2c Part 2). Exports the SAVED script; the
                Script tab's own buttons export the live editor state. */}
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Script</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleScriptPdf}
                  disabled={scriptExporting}
                  className="border border-border/40 px-3 py-2 text-left transition-colors hover:border-primary/60 disabled:opacity-50"
                >
                  <span className="block font-mono text-sm font-medium">
                    {scriptExporting === 'pdf' ? 'Exporting…' : 'Screenplay PDF'}
                  </span>
                  <span className="block text-[10px] text-muted-foreground leading-tight">Industry format</span>
                </button>
                <button
                  type="button"
                  onClick={handleScriptFountain}
                  disabled={scriptExporting}
                  className="border border-border/40 px-3 py-2 text-left transition-colors hover:border-primary/60 disabled:opacity-50"
                >
                  <span className="block font-mono text-sm font-medium">
                    {scriptExporting === 'fountain' ? 'Exporting…' : '.fountain'}
                  </span>
                  <span className="block text-[10px] text-muted-foreground leading-tight">Portable plain text</span>
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Exports the last saved version — use the Script tab's export for unsaved work.
              </p>
            </div>

            {/* Filename */}
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                {t('export_filename')}
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={filename}
                  onChange={e => setFilename(e.target.value)}
                  className="flex-1 bg-background border border-input px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="font-mono text-xs text-muted-foreground ml-1">.{activeFormat.ext}</span>
              </div>
            </div>

            {/* Download button */}
            <Button
              onClick={handleDownload}
              disabled={downloading || !selectedSections.length}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs py-3 shadow-glow mt-auto"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('export_downloading')}</>
              ) : (
                <><FileDown className="w-4 h-4 mr-2" /> {t('export_download_as', { format: activeFormat.label })}</>
              )}
            </Button>
          </div>

          {/* Right panel — preview */}
          <div className="flex-1 flex flex-col min-w-0 min-h-[240px] md:min-h-0">
            <div className="px-6 py-3 border-b border-border/40 flex-shrink-0">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t('export_preview')}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {selectedSections.length === 0 ? (
                <p className="text-muted-foreground font-mono text-sm">{t('export_noSections')}</p>
              ) : (
                <pre className="font-mono text-xs text-foreground leading-relaxed whitespace-pre-wrap break-words">
                  {preview}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Plot Tab ─────────────────────────────────────────── */
function PlotTab({ project, user, navigate, splitProps }) {
  const { t } = useTranslation();
  const currentLogline = project.logline || '';
  // Scroll-spy feed for the Plot Mentor (canvas mode): the act in view.
  const [actInView, setActInView] = useState(null);
  const progress = project.progress || 0;

  return (
    <div>
      <div className="mb-10 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">{t('plotForge')}</h2>
          <p className="text-muted-foreground">{t('ws_buildBeatSheet')}</p>
        </div>
        {splitProps && <SplitViewTrigger {...splitProps} />}
      </div>

      {/* No completion percentage in canvas mode — rail dots, the substance
          ribbon, and milestone marks carry depth (progress-signals design:
          reward depth, never box-filling). The legacy wizard keeps its bar. */}
      {!SCROLL_CANVAS && (
        <div className="mb-10 bg-card border border-border/40 p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-sm uppercase tracking-wider text-muted-foreground">{t('ws_overallProgress')}</span>
            <span className="font-mono text-sm text-primary">{progress}%</span>
          </div>
          <div className="h-2 bg-secondary relative">
            <div className="absolute inset-y-0 left-0 bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Setup card */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/plot/${project.project_id}/setup`)}
          className="w-full bg-card border border-border/40 hover:border-primary/50 p-6 text-left transition-all flex items-center justify-between group"
        >
          <div>
            <div className="font-mono text-sm uppercase tracking-widest text-muted-foreground mb-1">{t('ws_setup')}</div>
            <div className="font-mono text-lg font-semibold">{t('ws_loglineSynopsis')}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {currentLogline ? currentLogline : t('ws_addLogline')}
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </button>
      </div>

      {SCROLL_CANVAS ? (
        /* Continuous beat canvas — writing first, coaching second. */
        <div className="mb-16">
          <PlotCanvas projectId={project.project_id} onActiveActChange={setActInView} />
        </div>
      ) : (
        /* Acts (legacy wizard entry cards) */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {ACTS.map((act) => (
            <button
              key={act.level}
              onClick={() => navigate(`/plot/${project.project_id}/level/${act.level}`)}
              className="bg-card border border-border/40 hover:border-primary/50 p-6 text-left transition-all group"
            >
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {t('ws_act' + act.level)}
              </div>
              <div className="font-mono text-xl font-semibold mb-1 group-hover:text-primary transition-colors">
                {t('ws_act' + act.level)}
              </div>
              <div className="text-sm text-muted-foreground">{t(act.subtitleKey)}</div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary mt-4 transition-colors" />
            </button>
          ))}
        </div>
      )}

      {/* Plot Mentor — sharpened reflection-agent coaching on your beats */}
      <PlotMentor project={project} user={user} followAct={SCROLL_CANVAS ? actInView : undefined} />
    </div>
  );
}

/* ─── Plot Mentor ────────────────────────────────────────── */
const PLOT_MENTOR_MODES = [
  { key: 'forge_guide', labelKey: 'pm_forgeGuide', descKey: 'pm_forgeGuideDesc', icon: '🔥' },
  { key: 'stress_test', labelKey: 'pm_stressTest', descKey: 'pm_stressTestDesc', icon: '⚡' },
];

const ACT_TABS = [
  { act: null, labelKey: 'pm_fullStory' },
  { act: 1,    labelKey: 'pm_act1' },
  { act: 2,    labelKey: 'pm_act2' },
  { act: 3,    labelKey: 'pm_act3' },
];

function PlotMentor({ project, user, followAct }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('forge_guide');
  const [activeAct, setActiveAct] = useState(null);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [usesRemaining, setUsesRemaining] = useState(null);
  const bottomRef = useRef(null);

  // Canvas scroll-spy attachment: while the mentor is CLOSED, the act
  // selection follows the act in view, so opening it coaches what the
  // writer is looking at. It never fires a session (those are metered)
  // and never switches context mid-conversation — the act tabs keep
  // working exactly as before once a session is open.
  useEffect(() => {
    if (followAct == null || isOpen) return;
    setActiveAct(followAct);
  }, [followAct, isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const startSession = async (newMode, newAct, existingHistory) => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/ai/plot-mentor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.project_id,
          mode: newMode,
          act: newAct,
          history: existingHistory,
          language: i18n.language,
        }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upgrade to Indie for unlimited Plot Mentor access');
        setIsOpen(false);
        return;
      }
      if (!res.ok) {
        toast.error('Plot Mentor unavailable — try again');
        return;
      }
      const data = await res.json();
      setHistory([{ role: 'assistant', content: data.reply }]);
      if (data.uses_remaining !== undefined && data.uses_remaining !== null) {
        setUsesRemaining(data.uses_remaining);
      }
    } catch {
      toast.error('Plot Mentor unavailable — check your connection');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setHistory([]);
    setInput('');
    startSession(mode, activeAct, []);
  };

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setHistory([]);
    setInput('');
    startSession(newMode, activeAct, []);
  };

  const handleActSwitch = (newAct) => {
    setActiveAct(newAct);
    setHistory([]);
    setInput('');
    startSession(mode, newAct, []);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    const newHistory = [...history, { role: 'user', content: userMessage }];
    setHistory(newHistory);
    setLoading(true);
    try {
      const res = await authFetch(`${API}/ai/plot-mentor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.project_id,
          mode,
          act: activeAct,
          message: userMessage,
          history,
          language: i18n.language,
        }),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upgrade to Indie for unlimited Plot Mentor access');
        return;
      }
      if (!res.ok) { toast.error('Failed to get response'); return; }
      const data = await res.json();
      setHistory([...newHistory, { role: 'assistant', content: data.reply }]);
      if (data.uses_remaining !== undefined && data.uses_remaining !== null) {
        setUsesRemaining(data.uses_remaining);
      }
    } catch {
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const currentMode = PLOT_MENTOR_MODES.find(m => m.key === mode);

  return (
    <div className="mt-12">
      {/* Header row */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-mono text-lg font-semibold tracking-tight">{t('pm_title')}</h3>
            <p className="text-xs text-muted-foreground font-mono">
              {t('pm_desc')}
            </p>
          </div>
        </div>
        {!isIndiePlus && usesRemaining !== null && (
          <span className="font-mono text-xs text-muted-foreground border border-border/40 px-3 py-1.5">
            {t('pm_usesLeft', { n: usesRemaining })}
          </span>
        )}
      </div>

      {!isOpen ? (
        /* Collapsed state — mode buttons */
        <div className="bg-card border border-border/40 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {PLOT_MENTOR_MODES.map(m => (
              <button
                key={m.key}
                onClick={() => { setMode(m.key); }}
                className={`p-4 text-left border transition-all ${
                  mode === m.key
                    ? 'border-primary/60 bg-primary/10'
                    : 'border-border/40 hover:border-primary/40'
                }`}
              >
                <div className="font-mono text-sm font-semibold mb-1">
                  {m.icon} {t(m.labelKey)}
                </div>
                <div className="text-xs text-muted-foreground">{t(m.descKey)}</div>
              </button>
            ))}
          </div>
          {mode === 'forge_guide' && (
            <div className="flex gap-2 mb-6 flex-wrap">
              {ACT_TABS.map(a => (
                <button
                  key={String(a.act)}
                  onClick={() => setActiveAct(a.act)}
                  className={`px-3 py-1.5 font-mono text-xs transition-all ${
                    activeAct === a.act
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background border border-border/40 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t(a.labelKey)}
                </button>
              ))}
            </div>
          )}
          <Button
            onClick={handleOpen}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow"
          >
            <Brain className="w-4 h-4 mr-2" />
            {mode === 'forge_guide' ? t('pm_startForgeGuide') : t('pm_runStressTest')}
          </Button>
        </div>
      ) : (
        /* Open chat panel */
        <div className="bg-card border border-border/40">
          {/* Panel header */}
          <div className="flex items-center justify-between p-4 border-b border-border/40">
            <div className="flex items-center gap-3">
              <Brain className="w-4 h-4 text-primary" />
              <span className="font-mono text-sm font-semibold">
                {currentMode?.icon} {t(currentMode?.labelKey || '')}
              </span>
              {mode === 'forge_guide' && activeAct && (
                <span className="font-mono text-xs text-muted-foreground">· {t('pm_act' + activeAct)}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Mode switcher */}
              {PLOT_MENTOR_MODES.map(m => (
                <button
                  key={m.key}
                  onClick={() => handleModeSwitch(m.key)}
                  disabled={loading}
                  className={`px-2.5 py-1 font-mono text-xs transition-all disabled:opacity-50 ${
                    mode === m.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background border border-border/40 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {m.icon} {t(m.labelKey)}
                </button>
              ))}
              {/* Act switcher for forge_guide */}
              {mode === 'forge_guide' && (
                <div className="flex gap-1 ml-1">
                  {ACT_TABS.map(a => (
                    <button
                      key={String(a.act)}
                      onClick={() => handleActSwitch(a.act)}
                      disabled={loading}
                      className={`px-2 py-1 font-mono text-xs transition-all disabled:opacity-50 ${
                        activeAct === a.act
                          ? 'bg-primary/20 text-primary border border-primary/40'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t(a.labelKey)}
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground font-mono text-xs ml-2 px-2 py-1 border border-border/40"
              >
                {t('pm_close')}
              </button>
            </div>
          </div>

          {/* Chat area */}
          <div className="h-80 overflow-y-auto p-4 space-y-4">
            {history.length === 0 && loading && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-mono">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('pm_readingPlot')}</span>
              </div>
            )}
            {history.map((msg, i) => (
              <div
                key={i}
                className={
                  msg.role === 'assistant'
                    ? 'bg-primary/10 border border-primary/30 p-4 text-sm font-mono leading-relaxed'
                    : 'bg-secondary/40 p-3 text-sm text-right ml-8 font-mono leading-relaxed'
                }
              >
                {msg.role === 'assistant' && (
                  <div className="text-xs text-primary uppercase tracking-wider mb-2 font-mono">
                    {currentMode?.icon} {t(currentMode?.labelKey || '')}
                  </div>
                )}
                {msg.content}
              </div>
            ))}
            {loading && history.length > 0 && (
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>{t('pm_thinking')}</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border/40 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder={t('pm_replyPh')}
              disabled={loading}
              className="flex-1 bg-background border border-input px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-primary text-primary-foreground rounded-none px-3"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Characters Tab ─────────────────────────────────────── */
export function CharactersTab({ characters, plotProject, navigate, onRefresh, splitProps }) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const startRename = (char) => { setEditingId(char.project_id); setEditTitle(char.title || ''); };
  const cancelRename = () => { setEditingId(null); setEditTitle(''); };

  const saveRename = async (char) => {
    const title = editTitle.trim();
    if (!title || title === char.title) { cancelRename(); return; }
    try {
      const res = await authFetch(`${API}/projects/${char.project_id}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error();
      toast.success('Character renamed');
      cancelRename();
      onRefresh?.();
    } catch {
      toast.error('Failed to rename character');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await authFetch(`${API}/projects/${deleteTarget.project_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      toast.success('Character deleted');
      setDeleteTarget(null);
      onRefresh?.();
    } catch {
      toast.error('Failed to delete character');
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-10 flex-wrap">
        <div>
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">{t('characterLab')}</h2>
          <p className="text-muted-foreground">{t('ws_allCharacters')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {splitProps && <SplitViewTrigger {...splitProps} />}
          <Button
            onClick={() => navigate(`/create-project?type=character&workspace=${plotProject.project_id}`)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('newCharacter')}
          </Button>
        </div>
      </div>

      {characters.length === 0 ? (
        <EmptyState
          icon={User}
          title={t('ws_noCharacters')}
          description={t('ws_noCharactersDesc')}
          ctaLabel={t('ws_createFirstChar')}
          onCta={() => navigate(`/create-project?type=character&workspace=${plotProject.project_id}`)}
        />
      ) : (
        (() => {
          const ANTAG_TYPE_LABELS = {
            person: 'Person', institution: 'Institution', environment: 'Environment',
            internal_force: 'Internal Force', unknown: 'Unknown',
          };
          const groups = [
            { role: 'protagonist', label: 'Protagonist', chars: characters.filter(c => (c.character_role || 'protagonist') === 'protagonist') },
            { role: 'antagonist',  label: 'Opposition',  chars: characters.filter(c => c.character_role === 'antagonist') },
            { role: 'supporting',  label: 'Supporting',  chars: characters.filter(c => c.character_role === 'supporting') },
          ].filter(g => g.chars.length > 0);

          const renderCard = (char) => {
            const ArcIcon = ARC_ICONS[char.arc_type] || Minus;
            const antagonistTypeLabel = char.character_role === 'antagonist'
              ? ANTAG_TYPE_LABELS[char.antagonist_type || 'person']
              : null;
            const isEditing = editingId === char.project_id;
            return (
              <div
                key={char.project_id}
                onClick={() => { if (!isEditing) navigate(`/character/${char.project_id}/level/0`); }}
                className="bg-card border border-border/40 hover:border-primary/50 p-6 text-left transition-all hover:-translate-y-1 group cursor-pointer"
              >
                <div className="flex items-start gap-3 mb-4">
                  <ArcIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                        <Input
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          className="bg-background border-input rounded-none font-mono text-sm"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveRename(char); if (e.key === 'Escape') cancelRename(); }}
                        />
                        <Button size="sm" onClick={() => saveRename(char)} className="bg-primary text-primary-foreground rounded-none">Save</Button>
                      </div>
                    ) : (
                      <>
                        <div className="font-mono text-lg font-semibold truncate">{char.title}</div>
                        {char.level_0?.name && (
                          <div className="text-sm text-muted-foreground truncate">{char.level_0.name}</div>
                        )}
                      </>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => startRename(char)} className="h-7 w-7 p-0" aria-label="Rename character" data-testid={`rename-char-${char.project_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(char)} className="h-7 w-7 p-0" aria-label="Delete character" data-testid={`delete-char-${char.project_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-mono text-xs uppercase text-muted-foreground bg-muted/20 px-2 py-0.5 border border-border/30">
                    {antagonistTypeLabel ? `${antagonistTypeLabel} · opposition` : `${char.arc_type} · ${char.character_role}`}
                  </span>
                </div>
                <div className="h-1.5 bg-secondary relative">
                  <div className="absolute inset-y-0 left-0 bg-primary transition-all" style={{ width: `${char.progress || 0}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-mono">{t('dash_progress', { pct: char.progress || 0 })}</p>
              </div>
            );
          };

          return (
            <div className="space-y-12">
              {groups.map(group => (
                <div key={group.role}>
                  <div className="flex items-center gap-3 mb-6">
                    <h3 className="font-mono text-xs uppercase tracking-widest text-primary">{group.label}</h3>
                    <div className="flex-1 h-px bg-border/40" />
                    <span className="font-mono text-xs text-muted-foreground">{group.chars.length}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {group.chars.map(renderCard)}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        projectName={deleteTarget?.title || ''}
      />
    </div>
  );
}

/* ─── Scenes Tab ─────────────────────────────────────────── */
function ScenesTab({ scenes, plotProject, navigate, splitProps }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-10 flex-wrap">
        <div>
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">{t('sidebar_scenes')}</h2>
          <p className="text-muted-foreground">{t('ws_scenesDesc')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {splitProps && <SplitViewTrigger {...splitProps} />}
          <Button
            onClick={() => navigate(`/scenes?plot_project_id=${plotProject.project_id}`)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow"
          >
            <Clapperboard className="w-4 h-4 mr-2" />
            {t('scene_builder')}
          </Button>
        </div>
      </div>

      {scenes.length === 0 ? (
        <EmptyState
          icon={Clapperboard}
          title={t('ws_noScenes')}
          description={t('ws_noScenesDesc')}
          ctaLabel={t('ws_openSceneBuilder')}
          onCta={() => navigate(`/scenes?plot_project_id=${plotProject.project_id}`)}
        />
      ) : (
        <div className="space-y-3">
          {scenes.map((scene) => (
            <button
              key={scene.scene_id}
              onClick={() => navigate(`/scenes?plot_project_id=${plotProject.project_id}`)}
              className="w-full bg-card border border-border/40 hover:border-primary/50 p-5 text-left transition-all group flex items-center gap-4"
            >
              <Clapperboard className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-semibold truncate">{scene.title}</div>
                <div className="flex items-center gap-3 mt-1">
                  {scene.beat_key && (
                    <span className="font-mono text-xs text-muted-foreground uppercase">{scene.beat_key.replace(/_/g, ' ')}</span>
                  )}
                  {scene.location && (
                    <span className="font-mono text-xs text-muted-foreground">{scene.location}</span>
                  )}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Character Web Tab ──────────────────────────────────── */
export function CharacterWebTab({ characters, relationships, setRelationships, plotProject, user, navigate, splitProps }) {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProtagonist, setSelectedProtagonist] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [showConflictGauge, setShowConflictGauge] = useState(null);
  const [formData, setFormData] = useState({ moral_difference: '', tactical_difference: '', central_argument: '' });
  // null = creating; a relationship object = editing it
  const [editingRel, setEditingRel] = useState(null);

  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);

  const getCharacterName = (charId) => {
    const char = characters.find(c => c.project_id === charId);
    if (!char) return 'Unknown';
    return char.level_0?.name || char.title;
  };

  const getCharacterDisplayName = (char) => {
    const name = char.level_0?.name;
    return name ? `${name} (${char.title})` : char.title;
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingRel(null);
    setFormData({ moral_difference: '', tactical_difference: '', central_argument: '' });
    setSelectedProtagonist('');
    setSelectedTarget('');
  };

  const openAdd = () => {
    closeModal();
    setShowAddModal(true);
  };

  const openEdit = (rel) => {
    setEditingRel(rel);
    setSelectedProtagonist(rel.protagonist_id);
    setSelectedTarget(rel.target_id);
    setFormData({
      moral_difference: rel.moral_difference || '',
      tactical_difference: rel.tactical_difference || '',
      central_argument: rel.central_argument || '',
    });
    setShowAddModal(true);
  };

  const handleAddRelationship = async (e) => {
    e.preventDefault();
    if (!selectedProtagonist || !selectedTarget) { toast.error('Please select both characters'); return; }
    if (!formData.moral_difference || !formData.tactical_difference || !formData.central_argument) {
      toast.error('Please fill in all relationship fields');
      return;
    }
    try {
      if (editingRel) {
        const response = await authFetch(`${API}/relationships/${editingRel.relationship_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            // pair is fixed once created; backend ignores these on update
            protagonist_id: editingRel.protagonist_id,
            target_id: editingRel.target_id,
            ...formData,
          }),
        });
        if (response.status === 403) { navigate('/upgrade'); return; }
        if (!response.ok) throw new Error('Failed to update relationship');
        const updated = await response.json();
        setRelationships(prev => prev.map(r =>
          r.relationship_id === updated.relationship_id ? updated : r
        ));
        toast.success('Relationship updated');
        closeModal();
        return;
      }

      const response = await authFetch(`${API}/relationships`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          protagonist_id: selectedProtagonist,
          target_id: selectedTarget,
          ...formData,
        }),
      });
      if (response.status === 403) { navigate('/upgrade'); return; }
      if (!response.ok) throw new Error('Failed to create relationship');
      const newRel = await response.json();
      setRelationships(prev => [...prev, newRel]);
      toast.success('Relationship added');
      closeModal();
    } catch {
      toast.error(editingRel ? 'Failed to update relationship' : 'Failed to add relationship');
    }
  };

  const handleDeleteRelationship = async (relId) => {
    if (!window.confirm('Delete this relationship?')) return;
    try {
      await authFetch(`${API}/relationships/${relId}`, { method: 'DELETE', credentials: 'include' });
      setRelationships(prev => prev.filter(r => r.relationship_id !== relId));
      toast.success('Relationship deleted');
    } catch {
      toast.error('Failed to delete relationship');
    }
  };

  // Only show relationships involving this workspace's characters
  const workspaceCharIds = new Set(characters.map(c => c.project_id));
  const filteredRelationships = relationships.filter(
    rel => workspaceCharIds.has(rel.protagonist_id) || workspaceCharIds.has(rel.target_id)
  );

  if (!isIndiePlus) {
    return (
      <div className="bg-card border border-border/40 p-16 text-center max-w-lg mx-auto mt-10">
        <Network className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
        <h3 className="font-mono text-xl font-semibold mb-3">{t('ws_indieFeature')}</h3>
        <p className="text-muted-foreground mb-6">
          {t('ws_webIndieDesc')}
        </p>
        <Button
          onClick={() => navigate('/upgrade')}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
        >
          {t('char_upgradeToIndie')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-10 flex-wrap">
        <div>
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">{t('theWeb')}</h2>
          <p className="text-muted-foreground">{t('ws_webDesc')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {splitProps && <SplitViewTrigger {...splitProps} />}
          {characters.length >= 2 && (
            <Button
              onClick={openAdd}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('addRelationship')}
            </Button>
          )}
        </div>
      </div>

      {characters.length < 2 ? (
        <div className="bg-card border border-border/40 p-16 text-center">
          <Network className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
          <h3 className="font-mono text-xl font-semibold mb-3">Need at Least 2 Characters</h3>
          <p className="text-muted-foreground mb-6">Add more characters to this workspace to map relationships.</p>
          <Button
            onClick={() => navigate(`/create-project?type=character&workspace=${plotProject.project_id}`)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Character
          </Button>
        </div>
      ) : filteredRelationships.length === 0 ? (
        <div className="bg-card border border-border/40 p-16 text-center">
          <Network className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
          <h3 className="font-mono text-xl font-semibold mb-3">No Relationships Yet</h3>
          <p className="text-muted-foreground mb-6">Map how characters push and pull against each other.</p>
          <Button
            onClick={openAdd}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
          >
            Add First Relationship
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredRelationships.map((rel) => (
            <div key={rel.relationship_id} className="bg-card border border-border/40 p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-mono text-lg font-semibold">
                  {getCharacterName(rel.protagonist_id)} ↔ {getCharacterName(rel.target_id)}
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConflictGauge(rel)}
                    className="rounded-none text-xs font-mono"
                  >
                    <Gauge className="w-4 h-4 mr-1" />
                    Gauge
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(rel)}
                    aria-label="Edit relationship"
                    data-testid={`edit-rel-${rel.relationship_id}`}
                  >
                    <Pencil className="w-4 h-4 text-muted-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRelationship(rel.relationship_id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="font-mono text-xs uppercase text-primary mb-2">Moral Difference</p>
                  <p className="text-sm text-foreground">{rel.moral_difference}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-primary mb-2">Tactical Difference</p>
                  <p className="text-sm text-foreground">{rel.tactical_difference}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-primary mb-2">Central Argument</p>
                  <p className="text-sm text-foreground">{rel.central_argument}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Relationship Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={closeModal}>
          <div className="bg-card border border-primary/30 p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-mono text-2xl font-semibold mb-6">{editingRel ? 'Edit Relationship' : 'Add Relationship'}</h3>
            <form onSubmit={handleAddRelationship} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase">Character A</label>
                  <select
                    value={selectedProtagonist}
                    onChange={(e) => setSelectedProtagonist(e.target.value)}
                    disabled={!!editingRel}
                    className={`w-full bg-background border border-input rounded-none font-mono px-3 py-2 ${editingRel ? 'opacity-60 cursor-not-allowed' : ''}`}
                    required
                  >
                    <option value="">Select character</option>
                    {characters.map(c => (
                      <option key={c.project_id} value={c.project_id}>{getCharacterDisplayName(c)}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block font-mono text-sm uppercase">Character B</label>
                  <select
                    value={selectedTarget}
                    onChange={(e) => setSelectedTarget(e.target.value)}
                    disabled={!!editingRel}
                    className={`w-full bg-background border border-input rounded-none font-mono px-3 py-2 ${editingRel ? 'opacity-60 cursor-not-allowed' : ''}`}
                    required
                  >
                    <option value="">Select character</option>
                    {characters.filter(c => c.project_id !== selectedProtagonist).map(c => (
                      <option key={c.project_id} value={c.project_id}>{getCharacterDisplayName(c)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="block font-mono text-sm uppercase">Moral Difference</label>
                <AutoExpandTextarea
                  value={formData.moral_difference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, moral_difference: e.target.value }))}
                  placeholder="What fundamentally different core values do they hold?"
                  className="bg-background border-input rounded-none font-mono"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block font-mono text-sm uppercase">Tactical Difference</label>
                <AutoExpandTextarea
                  value={formData.tactical_difference || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, tactical_difference: e.target.value }))}
                  placeholder="How do their methods differ?"
                  className="bg-background border-input rounded-none font-mono"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block font-mono text-sm uppercase">Central Argument</label>
                <AutoExpandTextarea
                  value={formData.central_argument}
                  onChange={(e) => setFormData(prev => ({ ...prev, central_argument: e.target.value }))}
                  placeholder="What is the central debate they are having throughout the story?"
                  className="bg-background border-input rounded-none font-mono min-h-[100px]"
                  required
                />
              </div>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1 rounded-none font-mono uppercase tracking-widest text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs"
                >
                  {editingRel ? 'Save Changes' : 'Add Relationship'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Conflict Gauge Modal */}
      {showConflictGauge && (
        <ConflictGauge
          relationshipId={showConflictGauge.relationship_id}
          charAName={getCharacterName(showConflictGauge.protagonist_id)}
          charBName={getCharacterName(showConflictGauge.target_id)}
          isPro={isIndiePlus}
          onClose={() => setShowConflictGauge(null)}
        />
      )}
    </div>
  );
}

/* ─── Bible Tab ─────────────────────────────────────────── */
function BibleTab({ onExport, exporting, tier, splitProps }) {
  const isIndiePlus = ['indie', 'studio'].includes(tier);

  return (
    <div className="max-w-lg">
      <div className="mb-10 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">Story Bible</h2>
          <p className="text-muted-foreground">Export your complete story document.</p>
        </div>
        {splitProps && <SplitViewTrigger {...splitProps} />}
      </div>

      <div className="bg-card border border-border/40 p-8">
        <BookOpen className="w-12 h-12 text-primary mb-6" />
        <h3 className="font-mono text-lg font-semibold mb-2">Export PDF</h3>
        <p className="text-muted-foreground text-sm mb-6">
          {isIndiePlus
            ? 'Export a complete, watermark-free Story Bible with Courier Prime formatting.'
            : 'Export a Story Bible PDF. Upgrade to Indie to remove the watermark.'}
        </p>

        <div className="space-y-3 mb-8 text-sm text-muted-foreground font-mono">
          {['Cover page with project title', 'Full character sheets', 'Beat sheet by act', 'World building entries'].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <span className="text-primary">✓</span> {item}
            </div>
          ))}
        </div>

        <Button
          onClick={onExport}
          disabled={exporting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs py-3 shadow-glow"
        >
          <FileDown className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export Story Bible'}
        </Button>
      </div>
    </div>
  );
}
